// Stale-bundle / broken-deploy detection (P0-D, robustness hardening 2026-06-14).
//
// Symptom this guards against: a deploy prunes or mis-serves a worker/wasm asset,
// the server hands back a 404 — or worse, an HTML SPA-fallback page with a 200 —
// and the player loads "successfully" into a silent dead canvas. We make that
// loud: validate the asset is a real JS/WASM payload before we depend on it, and
// offer a one-shot guarded reload to pick up a fresh bundle.

export class StaleBundleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StaleBundleError';
  }
}

// Validate that a build-asset URL returns real JS/WASM, not a 404 or an HTML
// SPA-fallback page. Throws StaleBundleError on mismatch. `cache:'no-store'` so a
// stale CDN/disk-cache entry can't mask a broken deploy.
export async function assertFreshAsset(url: string, kind: 'worker' | 'wasm'): Promise<void> {
  let res: Response;
  try {
    res = await fetch(url, { cache: 'no-store' });
  } catch (e) {
    throw new StaleBundleError(`${kind} asset unreachable (${(e as Error).message}): ${url}`);
  }
  if (!res.ok) {
    throw new StaleBundleError(`${kind} asset HTTP ${res.status} ${res.statusText}: ${url}`);
  }
  const ct = (res.headers.get('content-type') ?? '').toLowerCase();
  // .mjs wasm-factory is served as JS; the .wasm itself as wasm/octet-stream.
  const typeOk = kind === 'wasm'
    ? /(javascript|ecmascript|wasm|octet-stream)/.test(ct)
    : /(javascript|ecmascript)/.test(ct);
  if (!typeOk) {
    throw new StaleBundleError(
      `${kind} asset has content-type '${ct || 'none'}' — expected JS/WASM. ` +
      `Likely a stale or broken deploy serving an HTML fallback: ${url}`);
  }
}

const RELOAD_FLAG = 'zap-stale-bundle-reloaded';

// One-shot guarded reload: reload to pick up a fresh bundle, but only once per
// tab session — if the deploy is genuinely broken a second reload would loop, so
// after the first attempt we let the caller surface the error instead. Returns
// true if a reload was triggered (caller should stop and let the page navigate).
export function maybeReloadForStaleBundle(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;  // already tried once
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    return false;   // no sessionStorage (private mode) → never auto-reload (no loop guard)
  }
  location.reload();
  return true;
}

// Clear the guard once a load fully succeeds, so a LATER stale deploy in the same
// long-lived tab can still earn one reload.
export function clearStaleBundleGuard(): void {
  try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
}
